import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { ValidationRuleTypeEnum } from "@/Common/Api/Validation/Dto/Responses/ValidationRuleType";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { CanadaState, CountryNames } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BritishColumbiaModuloCheck, OntarioModuloCheck } from "./ModuloCheck";

class HealthCardValidatorRequest {
    public readonly healthCardNumber: string;
    public readonly firstName: string;
    public readonly lastName: string;
    public readonly dateOfBirth: Date;
    public readonly genderLookupId: string;

    constructor(
        healthCardNumber: string,
        firstName?: string,
        lastName?: string,
        dateOfBirth?: Date,
        genderLookupId?: string
    ) {
        this.healthCardNumber = healthCardNumber;
        this.firstName = firstName;
        this.lastName = lastName;
        this.dateOfBirth = dateOfBirth;
        this.genderLookupId = genderLookupId;
    }
}

abstract class HealthCardValidator {
    public get healthCardLabel(): string {
        return cacheStore.GetString("HealthCard");
    }

    public get errorMessage(): string {
        return cacheStore.GetString("HealthCardIsNotValidLabel");
    }

    public get mask(): string {
        return this.validationRule?.EditMaskPattern || "";
    }

    public abstract canValidate(countryLookupId: string, regionLookupId: string): boolean;
    public abstract isValid(request: HealthCardValidatorRequest): boolean;

    protected abstract get countryLookupId(): string;
    protected abstract get regionLookupId(): string;

    protected get validationRule(): ValidationRule {
        const rulesForCountry = cacheStore.ValidationRules
            .filter(p => p.ValidationRuleTypeId === ValidationRuleTypeEnum.HealthIdentification && p.CountryLookupId === this.countryLookupId);

        if (this.regionLookupId === null)
            return rulesForCountry.firstOrDefault(p => IsNullOrUndefined(p.RegionLookupId));

        return rulesForCountry.firstOrDefault(p => p.RegionLookupId === this.regionLookupId) ||
            rulesForCountry.firstOrDefault(p => IsNullOrUndefined(p.RegionLookupId));
    }
}

abstract class CanadaHealthCardValidator extends HealthCardValidator {
    protected get countryLookupId(): string {
        return cacheStore.FindLookupByLookupCode(CountryNames.Canada).LookupID;
    }

    public canValidate(countryLookupId: string, regionLookupId: string): boolean {
        return this.countryLookupId === countryLookupId
            && this.regionLookupId === regionLookupId;
    }
}

class OntarioHealthCardValidator extends CanadaHealthCardValidator {
    protected get regionLookupId(): string {
        return cacheStore.FindLookupByLookupCode(CanadaState.Ontario).LookupID;
    }

    public isValid(request: HealthCardValidatorRequest): boolean {
        if (!request.healthCardNumber)
            return true;

        const validationRule = new ValidationHelper().getHealthcardValidationRule(this.countryLookupId, this.regionLookupId);

        let onModChecker = new OntarioModuloCheck();
        const regex = new RegExp(validationRule.ValidationRegexPattern, "i");
        const match = regex.exec(request.healthCardNumber);
        return !!match && onModChecker.isValid(match[1]);
    }
}

class BritishColumbiaHealthCardValidator extends CanadaHealthCardValidator {
    protected get regionLookupId(): string {
        return cacheStore.FindLookupByLookupCode("CanadaState_BC").LookupID;
    }

    public isValid(request: HealthCardValidatorRequest): boolean {
        if (!request.healthCardNumber)
            return true;

        let bcModChecker = new BritishColumbiaModuloCheck();
        return request.healthCardNumber[0] === "9"
            && bcModChecker.isValid(request.healthCardNumber);
    }
}

class QuebecHealthCardValidator extends CanadaHealthCardValidator {
    protected get regionLookupId(): string {
        return cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID;
    }

    public isValid(request: HealthCardValidatorRequest): boolean {
        let validationHelper = new ValidationHelper();
        return validationHelper.validateNAM(
            request.healthCardNumber,
            this.countryLookupId,
            this.regionLookupId,
            request.firstName,
            request.lastName,
            request.dateOfBirth,
            request.genderLookupId
        );
    }
}

class MexicoHealthCardValidator extends HealthCardValidator {
    public get healthCardLabel(): string {
        return "CURP";
    }

    public get errorMessage(): string {
        return cacheStore.GetString("CURPIsNotValidLabel");
    }

    protected get countryLookupId(): string {
        return cacheStore.FindLookupByLookupCode("CNMEXICO").LookupID;
    }

    protected get regionLookupId(): string {
        return null;
    }

    public canValidate(countryLookupId: string): boolean {
        return this.countryLookupId === countryLookupId;
    }

    public isValid(request: HealthCardValidatorRequest): boolean {
        if (!request.healthCardNumber)
            return true;

        var regexp = new RegExp(this.validationRule.ValidationRegexPattern, "i");
        return ValidationHelper.isExactMatch(request.healthCardNumber, regexp);
    }
}

class OtherHealthCardValidator extends HealthCardValidator {
    protected get countryLookupId(): string {
        return null;
    }

    protected get regionLookupId(): string {
        return null;
    }

    public canValidate(): boolean {
        return true;
    }

    public isValid(): boolean {
        return true;
    }
}

class HealthCardValidatorFactory {
    private static _validators = [
        new OntarioHealthCardValidator(),
        new BritishColumbiaHealthCardValidator(),
        new QuebecHealthCardValidator(),
        new MexicoHealthCardValidator(),
        new OtherHealthCardValidator()
    ];

    /**
     * Get a health card validator base on the country and region. If no country or region match,
     * it will return OtherHealthCardValidator as fallback
     * @param countryLookupId
     * @param regionLookupId
     */
    public static getValidator(countryLookupId: string, regionLookupId: string): HealthCardValidator {
        return this._validators.firstOrDefault(x => x.canValidate(countryLookupId, regionLookupId));
    }
}

export { HealthCardValidatorFactory, HealthCardValidatorRequest };
